import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import { motion } from "framer-motion";

const JavaFullStack = () => {
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  return (
    <div className=" text-gray-900 bg-white overflow-hidden">
      {/* Hero Section with Background Image */}
      <section className="relative py-28 px-6 md:px-20 text-center text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('/assets/java full.jpg')",
          }}
        >
          <div className="absolute inset-0  bg-opacity-70"></div>
        </div>

        <div className="relative z-10">
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-6 animate_animated animate_fadeInDown"
            data-aos="fade-down"
          >
            Java Full Stack Development
          </h1>
          <p
            className="text-lg md:text-xl max-w-2xl mx-auto mb-8 animate_animated animate_fadeInUp"
            data-aos="fade-up"
          >
            Master Java, Spring Boot, React, and modern full-stack technologies
            to become a professional developer.
          </p>
          <Link
            to="/contact"
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg transition-all animate_animated animate_zoomIn inline-block"
            data-aos="zoom-in"
          >
            Enroll Now
          </Link>
        </div>
      </section>

      {/* What is Java Full Stack (with subtle background) */}
      <section
        className="py-20 px-6 md:px-20 bg-fixed bg-cover bg-center text-gray-800"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1537432376769-00aabc1f7f89?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')",
        }}
      >
        <div className="bg-white bg-opacity-80 rounded-2xl shadow-xl p-10 max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" data-aos="fade-up">
            What is Java Full Stack Development?
          </h2>
          <p
            className="text-lg text-gray-700"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Java Full Stack Development refers to building applications using
            Java for backend and React for frontend. You’ll learn databases,
            APIs, and modern frameworks to create complete web apps.
          </p>
        </div>
      </section>

      {/* Syllabus Section */}
      <section className="py-20 px-6 md:px-20 bg-white">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Course Syllabus
        </h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {[
            {
              module: "Module 1: Java Basics",
              icon: "☕", // Java coffee icon
              topics: [
                "Java Syntax & Data Types",
                "Control Statements (if, switch, loops)",
                "OOP Concepts: Classes, Objects, Inheritance",
                "Exception Handling",
              ],
            },
            {
              module: "Module 2: Advanced Java & Spring Boot",
              icon: "⚡", // lightning icon
              topics: [
                "Collections & Generics",
                "Multithreading & Concurrency",
                "Spring Boot Basics",
                "Building RESTful APIs",
              ],
            },
            {
              module: "Module 3: Frontend with React",
              icon: "⚛️", // React icon
              topics: [
                "JSX & Components",
                "State & Props",
                "React Router & Hooks",
                "Integrating with APIs",
              ],
            },
            {
              module: "Module 4: Database & Deployment",
              icon: "🗄️", // database icon
              topics: [
                "MySQL, MongoDB, PostgreSQL Basics",
                "JPA & Hibernate",
                "Deploying Applications on Cloud",
                "Version Control with Git/GitHub",
              ],
            },
            {
              module: "Module 5: Advanced Topics",
              icon: "🚀", // rocket icon
              topics: [
                "Microservices Architecture",
                "Security in Spring Boot",
                "Performance Optimization",
                "Project & Portfolio Building",
              ],
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-gradient-to-r from-indigo-50 to-indigo-100 border-l-8 border-indigo-600 rounded-2xl p-8 shadow-lg hover:scale-105 hover:shadow-2xl transition-transform"
              data-aos="fade-up"
              data-aos-delay={i * 200}
            >
              <div className="flex items-center mb-4 animate-bounce">
                <span className="text-2xl mr-3">{item.icon}</span>
                <h3 className="text-xl font-semibold text-indigo-700">
                  {item.module}
                </h3>
              </div>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {item.topics.map((topic, idx) => (
                  <li key={idx}>{topic}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Course Outcomes Section */}
      <section className="py-20 px-6 md:px-20 bg-gray-50">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="zoom-in">
          Course Outcomes
        </h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {[
            {
              title: "Hands-on Projects",
              desc: "Build real-world apps with Java, Spring Boot & React.",
              icon: "🛠️", // tools
              color: "bg-yellow-100 text-yellow-700 border-yellow-400",
            },
            {
              title: "Career Ready",
              desc: "Prepare for Full Stack Developer, Java Developer roles.",
              icon: "🎯", // target
              color: "bg-green-100 text-green-700 border-green-400",
            },
            {
              title: "Advanced Skills",
              desc: "Master REST APIs, cloud deployment & microservices.",
              icon: "🚀", // rocket
              color: "bg-purple-100 text-purple-700 border-purple-400",
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`border-l-8 rounded-2xl p-8 shadow-lg hover:scale-105 hover:shadow-2xl transition-transform ${item.color}`}
              data-aos="fade-up"
              data-aos-delay={i * 200}
            >
              <div className="flex items-center mb-3 animate-pulse">
                <span className="text-3xl mr-3">{item.icon}</span>
                <h3 className="text-xl font-semibold">{item.title}</h3>
              </div>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Roles & Responsibilities with Split Background */}
      <section className="relative py-20 px-6 md:px-20 text-white">
        <div
          className="absolute inset-0 bg-fixed bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1518770660439-4636190af475?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <h2
            className="text-3xl font-bold text-center mb-12"
            data-aos="fade-up"
          >
            Roles & Responsibilities
          </h2>
          <div className="grid md:grid-cols-2 gap-10 text-lg">
            {/* LEFT COLUMN */}
            <div data-aos="fade-right" className="space-y-4">
              {[
                "Develop frontend apps using React.",
                "Build backend services using Java + Spring Boot.",
                "Create & maintain REST APIs.",
                "Work with MySQL, MongoDB, PostgreSQL.",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <motion.div
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <FaCheckCircle className="text-green-400" />
                  </motion.div>
                  <span>{text}</span>
                </div>
              ))}
            </div>

            {/* RIGHT COLUMN */}
            <div data-aos="fade-left" className="space-y-4">
              {[
                "Collaborate with UI/UX teams.",
                "Debug, test & deploy applications.",
                "Maintain Git/GitHub version control.",
                "Ensure performance & scalability.",
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-3">
                  <motion.div
                    animate={{ opacity: [0.2, 1, 0.2] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <FaCheckCircle className="text-green-400" />
                  </motion.div>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 md:px-20 text-center bg-indigo-600 text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-6" data-aos="fade-up">
          Start Your Java Full Stack Journey Today 🚀
        </h2>
        <p
          className="text-lg max-w-2xl mx-auto mb-8"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          Gain in-demand skills and real-world experience to launch your career
          as a professional developer.
        </p>
        <Link
          to="/contact"
          className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-3 rounded-full font-semibold shadow-lg transition-all"
          data-aos="zoom-in"
        >
          Enroll Now
        </Link>
      </section>
    </div>
  );
};

export default JavaFullStack;
